import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatINR, formatDate, BRAND } from "@/lib/api";
import WhatsAppShareDialog from "@/components/quickwash/WhatsAppShareDialog";
import { renderTag, buildTagData } from "@/components/quickwash/TagRenderer";
import { ArrowLeft, Printer, MessageCircle, Trash2, Receipt, Tag, BellRing, Pencil } from "lucide-react";

const STATUSES = ["Received", "Processing", "Dry Cleaning", "Ironing", "Ready", "Out for Delivery", "Delivered"];

const BillView = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [bill, setBill] = useState(null);
  const [waOpen, setWaOpen] = useState(false);
  const [waTemplate, setWaTemplate] = useState("bill_whatsapp");
  const [thermal, setThermal] = useState(false);
  const [tagsMode, setTagsMode] = useState(false);
  const [tagLayout, setTagLayout] = useState(null);

  const load = () => api.get(`/bills/${id}`).then((r) => setBill(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { api.get("/settings/tag-layout").then((r) => setTagLayout(r.data.layout)).catch(() => {}); }, []);

  const changeStatus = async (s) => {
    await api.patch(`/bills/${bill.id}/status`, { status: s });
    toast.success(`Status updated to ${s}`);
    await load();
    // When status becomes Ready or Out for Delivery, auto-open the reminder dialog.
    if (s === "Ready" || s === "Out for Delivery") {
      setWaTemplate("ready_for_delivery");
      setWaOpen(true);
    }
  };
  const shareBill = () => { setWaTemplate("bill_whatsapp"); setWaOpen(true); };
  const sendReadyReminder = () => { setWaTemplate("ready_for_delivery"); setWaOpen(true); };
  const del = async () => {
    if (!window.confirm("Delete this bill?")) return;
    await api.delete(`/bills/${bill.id}`);
    toast.success("Bill deleted");
    nav("/admin/bills");
  };

  if (!bill) return <div className="h-64 bg-slate-100 rounded-xl animate-pulse"/>;

  const doPrint = (mode) => {
    setThermal(mode === "thermal");
    setTagsMode(mode === "tags");
    setTimeout(() => window.print(), 150);
  };

  const totalPcs = bill ? bill.items.reduce((s, it) => s + Number(it.quantity || 0), 0) : 0;

  return (
    <div className="space-y-6" data-testid="bill-view-page">
      <div className="flex flex-wrap items-center gap-3 justify-between no-print">
        <Link to="/admin/bills" className="qw-btn-ghost" data-testid="bill-back"><ArrowLeft size={16}/> Back to Bills</Link>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => nav(`/admin/bills/${bill.id}/edit`)} className="qw-btn-primary" data-testid="bill-edit"><Pencil size={16}/>Edit Bill</button>
          <button onClick={() => doPrint("a4")} className="qw-btn-outline" data-testid="bill-print-a4"><Printer size={16}/>A4 Print</button>
          <button onClick={() => doPrint("thermal")} className="qw-btn-outline" data-testid="bill-print-thermal"><Receipt size={16}/>Thermal 80mm</button>
          <button onClick={() => doPrint("tags")} className="qw-btn-outline" data-testid="bill-print-tags"><Tag size={16}/>Print Tags (TSC)</button>
          <button onClick={shareBill} className="qw-btn-wa" data-testid="bill-whatsapp"><MessageCircle size={16}/>WhatsApp Bill</button>
          <button onClick={sendReadyReminder} className="qw-btn-accent" data-testid="bill-ready-reminder"><BellRing size={16}/>Send Ready Reminder</button>
          <button onClick={del} className="qw-btn-outline text-red-600 border-red-200 hover:border-red-400" data-testid="bill-delete"><Trash2 size={16}/>Delete</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 no-print">
        <div className="lg:col-span-2 qw-dash-card p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-xs uppercase font-semibold text-slate-500 tracking-widest">Invoice</div>
              <div className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Poppins' }}>{bill.bill_number}</div>
            </div>
            <div className="text-right">
              <div className="font-bold text-lg text-slate-900" style={{ fontFamily: 'Poppins' }}>Quick Wash</div>
              <div className="text-xs text-slate-500">{BRAND.address}</div>
              <div className="text-xs text-slate-500">📞 {BRAND.phone}</div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4 mb-6 text-sm">
            <div><div className="text-slate-500 text-xs uppercase">Billed To</div><div className="font-semibold text-slate-900">{bill.customer_name}</div><div>{bill.mobile}</div><div className="text-slate-600">{bill.address}</div></div>
            <div className="md:text-right"><div className="text-slate-500 text-xs uppercase">Order Date</div><div>{formatDate(bill.order_date)}</div><div className="text-slate-500 text-xs uppercase mt-2">Delivery Date</div><div>{formatDate(bill.delivery_date)}</div></div>
          </div>

          <table className="w-full text-sm mb-6">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left p-3">Item</th><th className="text-left p-3">Service</th><th className="text-right p-3">Qty / Wt</th><th className="text-right p-3">Rate</th><th className="text-right p-3">Amount</th></tr></thead>
            <tbody>{bill.items.map((it, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="p-3 font-medium">{it.item}</td>
                <td className="p-3 text-slate-600">{it.service}</td>
                <td className="p-3 text-right">
                  {it.unit === "kg" ? (
                    <>
                      <div>{it.weight_kg} kg</div>
                      <div className="text-xs text-slate-500">{it.quantity} pcs</div>
                    </>
                  ) : (
                    <>{it.quantity} pcs</>
                  )}
                </td>
                <td className="p-3 text-right">{formatINR(it.rate)}{it.unit === "kg" ? "/kg" : ""}</td>
                <td className="p-3 text-right font-semibold">{formatINR(it.amount)}</td>
              </tr>
            ))}</tbody>
          </table>

          <div className="flex justify-end">
            <div className="w-full md:w-72 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{formatINR(bill.subtotal)}</span></div>
              {bill.gst_amount > 0 && <div className="flex justify-between"><span className="text-slate-500">GST ({bill.gst_percent}%)</span><span>{formatINR(bill.gst_amount)}</span></div>}
              {bill.discount > 0 && <div className="flex justify-between text-emerald-600"><span>Discount</span><span>- {formatINR(bill.discount)}</span></div>}
              <div className="flex justify-between text-base border-t pt-2"><span className="font-bold">Grand Total</span><span className="font-bold text-blue-600">{formatINR(bill.grand_total)}</span></div>
              <div className="flex justify-between text-slate-500"><span>Advance</span><span>{formatINR(bill.advance)}</span></div>
              <div className="flex justify-between font-semibold"><span>Balance</span><span>{formatINR(bill.balance)}</span></div>
              <div className="flex justify-between text-xs text-slate-500 pt-2"><span>Payment mode</span><span>{bill.payment_mode}</span></div>
            </div>
          </div>
          {bill.notes && <div className="text-xs text-slate-500 mt-4 border-t pt-3">Notes: {bill.notes}</div>}
          <div className="text-[11px] text-slate-500 leading-relaxed mt-4 border-t pt-3 italic" data-testid="bill-terms">
            <span className="font-semibold not-italic text-slate-700">Terms & Conditions:</span> {BRAND.terms}
          </div>
        </div>

        <div className="qw-dash-card p-6">
          <div className="text-slate-900 font-semibold mb-3">Order Status</div>
          <div className="space-y-2">
            {STATUSES.map((s) => (
              <button key={s} onClick={() => changeStatus(s)} className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${bill.status === s ? 'bg-blue-50 text-blue-700 font-semibold border border-blue-200' : 'hover:bg-slate-50 text-slate-700'}`} data-testid={`bill-set-status-${s.replace(/\s/g, '-')}`}>{s}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Print Area */}
      <div className="print-area">
        {tagsMode ? (
          <div className="tags-print" data-testid="tags-print-area" style={{ gap: `${(tagLayout?.gap_mm ?? 2)}mm` }}>
            {(() => {
              const tags = [];
              let pieceNo = 0;
              const layout = tagLayout;
              if (!layout) return null;
              bill.items.forEach((it, itemIdx) => {
                for (let i = 1; i <= Number(it.quantity || 0); i++) {
                  pieceNo += 1;
                  const data = buildTagData(layout, bill, it, pieceNo, totalPcs, formatDate);
                  tags.push(renderTag(layout, data, `${itemIdx}-${i}`));
                }
              });
              return tags;
            })()}
          </div>
        ) : thermal ? (
          <div className="thermal-print p-2">
            <div className="text-center">
              <div className="font-bold text-lg">QUICK WASH</div>
              <div>{BRAND.address}</div>
              <div>Ph: {BRAND.phone} / {BRAND.phone2}</div>
              <div>------------------------------</div>
            </div>
            <div>Bill: {bill.bill_number}</div>
            <div>Date: {formatDate(bill.created_at)}</div>
            <div>Cust: {bill.customer_name}</div>
            <div>Mob : {bill.mobile}</div>
            <div>------------------------------</div>
            <div className="flex justify-between font-bold"><span>Item</span><span>Qty  Amt</span></div>
            {bill.items.map((it, i) => (
              <div key={i}>
                <div>{it.item} ({it.service})</div>
                <div className="flex justify-between">
                  <span>{it.unit === "kg" ? `${it.weight_kg}kg x ${it.rate}` : `${it.quantity} x ${it.rate}`}</span>
                  <span>{it.amount}</span>
                </div>
                {it.unit === "kg" && <div className="text-[10px]">({it.quantity} pcs)</div>}
              </div>
            ))}
            <div>------------------------------</div>
            <div className="flex justify-between">Subtotal <span>Rs.{bill.subtotal}</span></div>
            {bill.gst_amount > 0 && <div className="flex justify-between">GST <span>Rs.{bill.gst_amount}</span></div>}
            {bill.discount > 0 && <div className="flex justify-between">Discount <span>-Rs.{bill.discount}</span></div>}
            <div className="flex justify-between font-bold text-base">TOTAL <span>Rs.{bill.grand_total}</span></div>
            <div className="flex justify-between">Advance <span>Rs.{bill.advance}</span></div>
            <div className="flex justify-between">Balance <span>Rs.{bill.balance}</span></div>
            <div className="flex justify-between">Payment <span>{bill.payment_mode}</span></div>
            <div>------------------------------</div>
            <div>Delivery: {formatDate(bill.delivery_date)}</div>
            <div className="text-center mt-2">Thank You! Visit Again</div>
            <div className="text-center text-xs">Track: quickwash / {bill.bill_number}</div>
            <div className="text-[10px] mt-2 border-t border-black pt-1 leading-snug">
              <b>T&C:</b> {BRAND.terms}
            </div>
          </div>
        ) : (
          <div className="p-10">
            <div className="flex justify-between items-start mb-8 border-b pb-6">
              <div>
                <div className="text-3xl font-bold" style={{ fontFamily: 'Poppins' }}>Quick Wash</div>
                <div className="text-slate-600">{BRAND.address}</div>
                <div className="text-slate-600">📞 {BRAND.phone} · {BRAND.phone2}</div>
              </div>
              <div className="text-right"><div className="text-xs uppercase text-slate-500">Invoice</div><div className="text-2xl font-bold">{bill.bill_number}</div><div className="text-sm text-slate-500">{formatDate(bill.created_at)}</div></div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div><div className="text-xs uppercase text-slate-500 mb-1">Billed To</div><div className="font-bold text-lg">{bill.customer_name}</div><div>{bill.mobile}</div><div className="text-slate-600">{bill.address}</div></div>
              <div className="text-right"><div className="text-xs uppercase text-slate-500 mb-1">Delivery Date</div><div className="font-bold">{formatDate(bill.delivery_date)}</div><div className="text-xs uppercase text-slate-500 mt-3">Status</div><div className="font-semibold">{bill.status}</div></div>
            </div>
            <table className="w-full text-sm border-collapse">
              <thead><tr className="border-b-2 border-slate-900"><th className="text-left p-2">#</th><th className="text-left p-2">Item</th><th className="text-left p-2">Service</th><th className="text-right p-2">Qty / Weight</th><th className="text-right p-2">Rate</th><th className="text-right p-2">Amount</th></tr></thead>
              <tbody>{bill.items.map((it, i) => (
                <tr key={i} className="border-b border-slate-200">
                  <td className="p-2">{i+1}</td>
                  <td className="p-2 font-medium">{it.item}</td>
                  <td className="p-2">{it.service}</td>
                  <td className="p-2 text-right">
                    {it.unit === "kg" ? (
                      <><div>{it.weight_kg} kg</div><div className="text-xs">({it.quantity} pcs)</div></>
                    ) : (
                      <>{it.quantity} pcs</>
                    )}
                  </td>
                  <td className="p-2 text-right">₹{it.rate}{it.unit === "kg" ? "/kg" : ""}</td>
                  <td className="p-2 text-right">₹{it.amount}</td>
                </tr>
              ))}</tbody>
            </table>
            <div className="flex justify-end mt-6">
              <div className="w-80 space-y-1 text-sm">
                <div className="flex justify-between"><span>Subtotal</span><span>₹{bill.subtotal}</span></div>
                {bill.gst_amount > 0 && <div className="flex justify-between"><span>GST ({bill.gst_percent}%)</span><span>₹{bill.gst_amount}</span></div>}
                {bill.discount > 0 && <div className="flex justify-between"><span>Discount</span><span>- ₹{bill.discount}</span></div>}
                <div className="flex justify-between text-lg font-bold border-t-2 border-slate-900 pt-2"><span>Grand Total</span><span>₹{bill.grand_total}</span></div>
                <div className="flex justify-between"><span>Advance ({bill.payment_mode})</span><span>₹{bill.advance}</span></div>
                <div className="flex justify-between font-semibold"><span>Balance Due</span><span>₹{bill.balance}</span></div>
              </div>
            </div>
            <div className="mt-12 text-xs text-slate-700 border-t pt-4 leading-relaxed">
              <div className="font-bold text-slate-900 mb-1">Terms &amp; Conditions</div>
              <div className="italic">{BRAND.terms}</div>
            </div>
            <div className="mt-4 text-center text-xs text-slate-500 border-t pt-4">Thank you for choosing Quick Wash. For queries, call {BRAND.phone} / {BRAND.phone2}.</div>
          </div>
        )}
      </div>

      <WhatsAppShareDialog open={waOpen} onOpenChange={setWaOpen} bill={bill} templateKey={waTemplate}/>
    </div>
  );
};

export default BillView;
